import { UseMutateFunction, useMutation, useQuery } from "@tanstack/react-query";
import { CreateCommanWay, GetCommanWay, ListCommanWay, UpdateBookingCommanWay, UpdateCommanWay } from "API/commonApi";
import axios, { AxiosResponse } from "axios";
import { InputTable } from "components/DataTable/InputTable";
import { useFormik } from "formik";
import { debounce } from "lodash";
import moment from "moment";
import { any } from "prop-types";
import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Select from "react-select";
import UseToast from "utils/AfToast";
import { constantValues, roles } from "utils/constant";
import handleWheel from "utils/handleOnWheel";
import { AfDataType as ADT } from "utils/TableColumnData";
import { number, object, string } from "yup";
import { BookingInputTable } from "./BookingInputTable";
import Swal from "sweetalert2";
import { IntlBookingInputTable } from "./IntlBookingInputTable";
import { request } from "config/request";
import Spinner from "utils/Spinner";
import { Button } from "@heroui/react";

// Modern select styles
const bookingSelectStyles = {
  control: (base: any, state: any) => ({
    ...base,
    borderRadius: "10px",
    borderColor: state.isFocused ? "#6366f1" : "#e2e8f0",
    minHeight: "28px",
    fontSize: "12px",
    boxShadow: state.isFocused ? "0 0 0 2px rgba(99,102,241,0.15)" : "none",
    "&:hover": { borderColor: "#cbd5e1" },
    backgroundColor: "white",
    transition: "all 0.15s ease",
  }),
  valueContainer: (base: any) => ({ ...base, padding: "0 6px" }),
  input: (base: any) => ({ ...base, margin: 0, padding: 0 }),
  indicatorsContainer: (base: any) => ({ ...base, height: "28px" }),
  dropdownIndicator: (base: any) => ({ ...base, padding: "4px" }),
  clearIndicator: (base: any) => ({ ...base, padding: "4px" }),
  option: (base: any, state: any) => ({
    ...base,
    fontSize: "12px",
    padding: "6px 10px",
    backgroundColor: state.isSelected ? "#6366f1" : state.isFocused ? "#f1f5f9" : "white",
    color: state.isSelected ? "white" : "#334155",
    "&:active": { backgroundColor: "#e0e7ff" },
  }),
  menu: (base: any) => ({ ...base, borderRadius: "12px", overflow: "hidden", boxShadow: "0 10px 40px rgba(0,0,0,0.1)" }),
  menuList: (base: any) => ({ ...base, padding: "4px" }),
  singleValue: (base: any) => ({ ...base, color: "#334155" }),
  placeholder: (base: any) => ({ ...base, color: "#94a3b8", fontSize: "12px" }),
};

// MInput Component
const MInput = ({
  label,
  name,
  value,
  onChange,
  onBlur,
  onBlurExtra,
  type = "text",
  disabled = false,
  className = "",
  placeholder = "",
  min,
  max,
  labelClassName = "text-slate-500",
}: any) => (
  <div className={`flex items-center gap-1.5 ${className}`}>
    {label && <label className={`text-[11px] font-medium whitespace-nowrap min-w-[52px] text-right ${labelClassName}`}>{label}</label>}
    <input
      type={type}
      className={`flex-1 min-w-0 px-2 py-[3px] rounded-lg border text-[13px] outline-none transition-all ${
        disabled
          ? "border-slate-200 bg-slate-100 text-slate-500"
          : "border-slate-200 bg-white focus:border-primary2 focus:ring-2 focus:ring-primary2/20 text-slate-700"
      }`}
      name={name}
      value={value ?? ""}
      onChange={onChange}
      onBlur={(e) => {
        onBlur && onBlur(e);
        onBlurExtra && onBlurExtra(e);
      }}
      onWheel={handleWheel}
      placeholder={placeholder || label}
      disabled={disabled}
      min={min}
      max={max}
    />
  </div>
);

// MSelect Component
const MSelect = ({ label, name, options, value, onChange, onBlur, disabled = false, className = "", labelClassName = "text-slate-500" }: any) => (
  <div className={`flex items-center gap-1.5 ${className}`}>
    {label && <label className={`text-[11px] font-medium whitespace-nowrap min-w-[52px] text-right ${labelClassName}`}>{label}</label>}
    <Select
      className="flex-1 min-w-0"
      options={options}
      value={value}
      onChange={onChange}
      onBlur={onBlur}
      isDisabled={disabled}
      styles={bookingSelectStyles}
      menuPortalTarget={document.body}
      menuPosition="fixed"
    />
  </div>
);

// MCheckbox Component
const MCheckbox = ({ label, name, value, onChange, disabled = false, className = "", labelClassName = "" }: any) => (
  <label className={`inline-flex items-center gap-1.5 px-2 py-1.5 rounded-lg border transition-all cursor-pointer text-[11px] font-medium ${className || "border-slate-200 bg-white hover:border-slate-300 text-slate-600"}`}>
    <input
      type="checkbox"
      name={name}
      checked={value == 1 || value === true}
      onChange={onChange}
      disabled={disabled}
      className="w-3.5 h-3.5 rounded border-slate-300 text-primary2 focus:ring-primary2/20"
    />
    <span className={labelClassName || ""}>{label}</span>
  </label>
);

type OptionType = {
  value: number;
  label: string;
};

interface BookingState {
  mode?: string;
  data?: { [key: string]: any }; // Adjust the type based on your data structure
  // Add other properties if needed
}
declare global {
  interface Window {
    SEND_WAPP: (num:any, link:any, msg: any) => void;
  }
}

const AddIntlBooking: React.FC = ({}) => {
  let isDisabled = false;
  const BASE_URL = process.env.REACT_APP_BACKEND_URL;

  const navigate = useNavigate();
  const { state } = useLocation();
  const { mode = "add", data = {} } = (state || {}) as BookingState; // Check for existence and type assertion

  const [isBranchSet, setIsBranchSet] = useState<any>();
  const [newTableData, setNewTableData] = useState<any>([]);
  const [nestedTables, setNestedTables] = useState<any>({});
  const [pincodeSection, setPincodeSection] = useState<any>("");
  const [userDetails, setUserDetails] = useState<any>({});
  const [subbranchOpt, setSubBranchOpt] = useState<any>();
  const [providerOpt, setProviderOpt] = useState<any>();

  const [isDateDisabled, setIsDateDisabled] = useState<boolean>(false);
  const [bookingData, setBookingData] = useState<any>();
  const [billingDiffFromShipping, setBillingDiffFromShipping] = useState(false);
  const [gstSearching, setGstSearching] = useState(false);

  const isValidGSTIN = (gstin: string) => {
    if (!gstin || gstin.length !== 15) return false;
    return /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/.test(gstin.toUpperCase());
  };

  const handleGstSearch = (gstin: string) => {
    const upper = gstin.toUpperCase().trim();
    if (!isValidGSTIN(upper)) {
      if (upper.length > 0) UseToast("Invalid GSTIN format", "error");
      return;
    }
    setGstSearching(true);
    GetCommanWay(`/admin/gst/search/${upper}`, {}, {})
      .then((data: any) => {
        if (data.status === "SUCCESS" && data.data) {
          const d = data.data;
          const addrParts = [d.AddrFlno, d.AddrBno, d.AddrBnm, d.AddrSt, d.AddrLoc].filter(Boolean).map((s: string) => s.trim()).filter(Boolean);
          setFieldValue("billing_gst_no", d.Gstin || upper);
          setFieldValue("billing_name", d.LegalName || d.TradeName || "");
          setFieldValue("billing_address", addrParts.join(", "));
          setFieldValue("billing_pincode", d.AddrPncd ? String(d.AddrPncd) : "");
          setFieldValue("billing_city", d.AddrLoc ? d.AddrLoc.trim() : "");
          if (d.AddrPncd && String(d.AddrPncd).length === 6) {
            setPincodeSection("billing");
            mutatePincode(String(d.AddrPncd));
          }
          UseToast("GST details loaded", "success");
        } else {
          UseToast(data.message || "GST not found", "error");
        }
      })
      .catch((err: any) => {
        UseToast(typeof err === "string" ? err : err.message || "GST search failed", "error");
      })
      .finally(() => setGstSearching(false));
  };

  const SEND_WAPP = (num: any, link: any, msg: any) => {
      axios.request({
        url: BASE_URL + `/admin/send-wapp`,
        method: "POST",
        data: {
          mobile: num,
          pdf: link,
          msg: msg,
        },
      });
      alert("Whatsapp Sent Successfully!");
    };

  useEffect(() => {
    const userD = (() => { const auth = localStorage.getItem("auth"); return auth ? JSON.parse(auth) : {}; })();
    if (Object.keys(userD).length) {
      setUserDetails(userD.state.user);
      console.log("userD.state", userD.state.user);
      if (userD.state.user.roleName !== roles.SUPER_ADMIN && userD.state.user.roleName !== roles.BRANCH_ADMIN) {
        setSubBranchOpt(userD.state.user.branch_name);
      }

      if (userD.state.user.roleName !== roles.SUPER_ADMIN) {
        setIsDateDisabled(true);
      }
      fetchDBInputData();
    }
    window.SEND_WAPP = SEND_WAPP;
  }, []);

  


  const columnData = [
    {
      label: "NO.",
      key: "id",
      type: ADT.TEXT,
      initialValue: "",
      view: true,
      edit: false,
      add: true,
      filter: true,
      sort: true,
    },
    {
      label: "Pieces",
      key: "piece",
      type: "PIECE",
      initialValue: [],
      view: true,
      add: true,
      edit: false,
      filter: false,
      sort: true,
    },
    {
      label: "AWB NO.",
      key: "awb_no",
      type: ADT.TEXT,
      initialValue: "",
      view: true,
      edit: false,
      add: true,
      filter: true,
      sort: true,
      css: "w-32",
    },
    {
      label: "Product",
      key: "autoGen_product",
      type: ADT.TEXT,
      initialValue: "",
      view: false,
      add: true,
      edit: true,
      filter: false,
      sort: false,
    },
    {
      label: "Weight",
      key: "weight",
      type: ADT.NUMBER,
      initialValue: 0,
      view: true,
      add: true,
      edit: true,
      filter: true,
      sort: true,
      css: "w-[55px]",
    },
    {
      label: "Zipcode",
      key: "pincode",
      type: ADT.TEXT,
      initialValue: "",
      view: true,
      add: true,
      edit: false,
      filter: true,
      sort: true,
      css: "w-[100px]"
    },
    {
      label: "City",
      key: "consignee_city",
      type: ADT.TEXT,
      initialValue: "",
      view: true,
      add: true,
      edit: true,
      filter: true,
      sort: true,
      css: "w-[120px]",
    },
    {
      label: "State",
      key: "consignee_state",
      type: ADT.TEXT,
      initialValue: "",
      view: true,
      add: true,
      edit: true,
      filter: true,
      sort: true,
      css: "w-[120px]",
    },
    {
      label: "Country",
      key: "consignee_country",
      type: ADT.DB,
      options: nestedTables?.consignee_country || [],
      db: "country",
      db_col: "country_name",
      initialValue: false,
      view: true,
      add: true,
      edit: false,
      filter: true,
      sort: true,
      css: "w-[170px]",
    },
    {
      label: "Contact No",
      key: "contact_no",
      type: ADT.TEXT,
      initialValue: "",
      view: true,
      add: true,
      edit: false,
      filter: true,
      sort: true,
      css: "w-32",
    },
    {
      label: "Consignee",
      key: "consignee",
      type: ADT.TEXT,
      initialValue: "",
      view: true,
      add: true,
      edit: false,
      filter: true,
      sort: true,
    },
    {
      label: "Address",
      key: "address",
      type: ADT.TEXT,
      initialValue: "",
      view: true,
      add: true,
      edit: false,
      filter: true,
      sort: true,
    },
    {
      label: "Dox/Non Dox",
      key: "dox_or_non_dox",
      type: ADT.SELECT,
      options: [
        { value: true, label: "DOX" },
        { value: false, label: "NON DOX" },
      ],
      initialValue: true,
      view: true,
      add: true,
      edit: false,
      filter: true,
      sort: true,
      css: "w-24",
    },
  ];

  const {
    mutate: priceCheckMutate,
    isLoading: isloadingPrice,
  }: {
    mutate: UseMutateFunction<AxiosResponse<any, any>, unknown, any, unknown>;
    isLoading: any;
  } = useMutation((data) => CreateCommanWay(data), {
    onSuccess: (data: any) => {
      console.log(data, data.status == "SUCCESS");
      if (data.status == "SUCCESS") {
        Swal.fire({
          title: "Are you sure!",
          text: data.data == "NA" ? "---" : "Price: " + ((data.data.price + data.data.tax).toFixed(0)),
          icon: "info",
          confirmButtonText: "Do Confirm Booking",
          showCancelButton: true,
        }).then((x) => {
          if (x.isConfirmed) {
            console.log(bookingData);
            //return;
            mutate({ url: "/admin/booking/create", data: bookingData });
          }
        });
      } else {
        UseToast(data.message, "error");
      }
    },
    onError: (data: any) => {
      typeof data === "string" ? UseToast(data, "error") : UseToast(data.message, "error");
    },
  });

  const {
    mutate,
    isLoading: isloading,
  }: {
    mutate: UseMutateFunction<AxiosResponse<any, any>, unknown, any, unknown>;
    isLoading: any;
  } = useMutation((data) => CreateCommanWay(data), {
    onSuccess: (data: any) => {
      console.log(data, data.status == "SUCCESS");
      if (data.status == "SUCCESS") {
        //UseToast("Data Added Successfully", "success");
        resetForm();
        setNewTableData([]);
        //successNavigate();
        Swal.fire({
          title: "Booking Successful",
          text: data.data?.pdfPath,
          html: data.data?.pdfPath ? `
          <input id="shipper_mobile_wapp" type="text" value="${values.shipper_mobile}"/>
          <a onClick="window.SEND_WAPP('${values.shipper_mobile}', '${BASE_URL}${data.data?.pdfPath}', '${newTableData[0].awb_no}')" class="text-primary underline cursor-pointer">Send Whatsapp</a><br><br>
          <a class="text-primary underline" href='${BASE_URL}${data.data?.pdfPath}' target="_blank">Download Invoice</a>` : 'Thank you!',
          icon: "info",
          showConfirmButton: false,
          showCancelButton: true,
        }).then((x) => {});

      } else {
        UseToast(data.message, "error");
      }
    },
    onError: (data: any) => {
      typeof data === "string" ? UseToast(data, "error") : UseToast(data.message, "error");
    },
  });

  const {
    mutate: updateBookingMutate,
    isLoading: isupdateBookingloading,
  }: {
    mutate: UseMutateFunction<AxiosResponse<any, any>, unknown, any, unknown>;
    isLoading: any;
  } = useMutation((data) => UpdateCommanWay(data), {
    onSuccess: (data: any) => {
      console.log(data, data.status == "SUCCESS");
      if (data.status == "SUCCESS") {
        UseToast("Data Updated Successfully", "success");
        navigate(-1);
      } else {
        UseToast(data.message, "error");
      }
    },
    onError: (data: any) => {
      typeof data === "string" ? UseToast(data, "error") : UseToast(data.message, "error");
    },
  });

  const handleQuantityChange = (e, doLoadPickup?) => {
    let quantity = parseInt(e.target.value);
    if (quantity > 99) {
      setFieldValue("quantity", 99);
      return;
    }
    setNewTableData((prevData) => {
      const columnKeys = columnData.map((d) => d.key);

      let newData: any[] = [];
      let startIdx = 0;

      if (doLoadPickup) {
      } else {
        if (quantity <= prevData.length) {
          return prevData.slice(0, quantity);
        }
        startIdx = prevData.length;
        newData = [...prevData];
      }

      for (let index = startIdx; index < quantity; index++) {
        const obj = {};
        for (let j = 0; j < columnKeys.length; j++) {
          obj[columnKeys[j]] = columnData.find((column) => column.key === columnKeys[j])?.initialValue;
        }
        obj["id"] = newData[newData.length - 1]?.id ? newData[newData.length - 1].id + 1 : 1;

        if (doLoadPickup && doLoadPickup.length) {
          obj["pincode"] = doLoadPickup[0].consignee_pincode;
          obj["contact_no"] = doLoadPickup[0].consignee_mobile;
          obj["consignee"] = doLoadPickup[0].consignee_cgnee;
          obj["address"] = doLoadPickup[0].consignee_address;
          obj["dox_or_non_dox"] = doLoadPickup[0].type == "DOX" ? true : false;
        }

        newData.push(obj);
      }
      return newData;
    });
  };

  const { handleSubmit, handleBlur, values, handleChange, errors, setValues, setErrors, touched, dirty, setFieldValue, resetForm, isSubmitting } =
    useFormik({
      validationSchema: object().shape({}),
      initialValues: {
        date: moment().format("YYYY-MM-DD"),
        quantity: 0,
        autoGen: 0,
        type: "WI",
        pickup_code: "",
        customer_code: "",
        remarks: "",
        weight: 0,
        ten_30: { value: 0 },
        project_code: "",
        product_name: { value: 0, label: "" },
        holiday: { value: 0 },

        customer_pincode: "",
        customer_mobile: "",
        customer_sh: "",
        customer_city: "",
        customer_state: "",
        customer_country: "",
        customer_address: "",
        consignee_pincode: "",
        consignee_mobile: "",
        consignee_cgnee: "",
        consignee_city: "",
        consignee_state: "",
        consignee_country: "",
        consignee_address: "",

        shipper_pincode: "",
        shipper_mobile: "",
        shipper_sh: "",
        shipper_city: "",
        shipper_state: "",
        shipper_country: "",
        shipper_pan: "",
        shipper_gst_no: "",
        shipper_address: "",
        change_shipper: false,

        billing_name: "",
        billing_address: "",
        billing_city: "",
        billing_state: "",
        billing_pincode: "",
        billing_country: "India",
        billing_gst_no: "",
        billing_mobile: "",

        oda_charge: 0,
        pkg_charge: 0,
        d_ice_charge: 0,

        branch_id: { value: 0, label: "" },
        subbranch: { value: 0, label: "select branch" },
        provider: { value: 0, label: "Select Provider" },
      },
      onSubmit: async (values, { resetForm }) => {
        if (mode && mode === "edit") {
          updateAwb({ data: newTableData, bookingId: data.id });
          let req: any = { ...values };
          req.type = req.type;
          req.ten_30 = req.ten_30 ? req.ten_30.value : undefined;
          req.product_name = req.product_name ? req.product_name.value : undefined;
          req.holiday = req.holiday === 1 ? true : false;

          req.weight = parseFloat(req.weight).toString();
          req.shipper_mobile = Number(req.shipper_mobile);
          req.consignee_mobile = Number(req.consignee_mobile);
          req.change_shipper = req.change_shipper.toString();

          if (billingDiffFromShipping) {
            req.customer_code = "";
            req.customer_pincode = "";
            req.customer_mobile = 0;
            req.customer_sh = "";
            req.customer_city = "";
            req.customer_state = "";
            req.customer_country = "";
            req.customer_address = "";
            req.billing_mobile = req.billing_mobile ? Number(String(req.billing_mobile).replaceAll(" ", "")) : "";
          } else {
            req.customer_mobile = Number(req.customer_mobile);
            delete req.billing_name;
            delete req.billing_address;
            delete req.billing_city;
            delete req.billing_state;
            delete req.billing_pincode;
            delete req.billing_country;
            delete req.billing_gst_no;
            delete req.billing_mobile;
          }

          req.oda_charge = parseFloat(req.oda_charge);
          req.pkg_charge = parseFloat(req.pkg_charge);
          req.d_ice_charge = parseFloat(req.d_ice_charge);
          userDetails?.roleName !== roles.SUPER_ADMIN && userDetails?.roleName !== roles.BRANCH_ADMIN
            ? (req.subbranch = userDetails.subbranch)
            : (req.subbranch = req.subbranch ? req.subbranch.value : null);

          req["awb_data"] = newTableData;
          req.provider = req.provider.value;
          delete req.id;
          delete req?.createdAt;
          constantValues.defaultFields.forEach((d) => delete req[d]);

          console.log(req);

          updateBookingMutate({
            url: "/admin/booking/update/" + data.id,
            data: req,
          });
          //return;
        }
        let req: any = { ...values };
        let awbError = "";

        req.type = req.type;
        req.ten_30 = req.ten_30 ? req.ten_30.value : undefined;
        req.product_name = req.product_name ? req.product_name.value : undefined;
        req.holiday = req.holiday === 1 ? true : false;

        req.weight = parseFloat(req.weight).toString();
        req.shipper_mobile = Number(req.shipper_mobile);
        req.consignee_mobile = Number(req.consignee_mobile);
        req.change_shipper = req.change_shipper.toString();
        req.provider = req.provider.value;

        if (billingDiffFromShipping) {
          req.customer_code = "";
          req.customer_pincode = "";
          req.customer_mobile = 0;
          req.customer_sh = "";
          req.customer_city = "";
          req.customer_state = "";
          req.customer_country = "";
          req.customer_address = "";
          req.billing_mobile = req.billing_mobile ? Number(String(req.billing_mobile).replaceAll(" ", "")) : "";
        } else {
          req.customer_mobile = Number(req.customer_mobile);
          delete req.billing_name;
          delete req.billing_address;
          delete req.billing_city;
          delete req.billing_state;
          delete req.billing_pincode;
          delete req.billing_country;
          delete req.billing_gst_no;
          delete req.billing_mobile;
        }

        req.oda_charge = parseFloat(req.oda_charge);
        req.pkg_charge = parseFloat(req.pkg_charge);
        req.d_ice_charge = parseFloat(req.d_ice_charge);
        userDetails?.roleName !== roles.SUPER_ADMIN && userDetails?.roleName !== roles.BRANCH_ADMIN
          ? (req.subbranch = userDetails.subbranch)
          : (req.subbranch = req.subbranch ? req.subbranch.value : null);
        newTableData.forEach((a) => {
          if (req.autoGen) {
            a.autoGen_product = "I";
            a.prefix = "I";
          } else {
            a.prefix = a.awb_no[0]?.toUpperCase();
          }
          a.provider = req.provider;
          a.destination = "INTERNATIONAL";
          nestedTables?.consignee_country.forEach((c) => {
            if (c.value == a.consignee_country) {
              a.destination = c.label;
            }
          });
        });
        req["awb_data"] = newTableData;

        delete req.id;
        delete req?.createdAt;
        constantValues.defaultFields.forEach((d) => delete req[d]);

        try {
          req.consignee_country = newTableData[0].consignee_country;
          req.consignee_address = newTableData[0].consignee_address;
          req.consignee_cgnee = newTableData[0].consignee_cgnee;
          req.consignee_city = newTableData[0].consignee_city;
          req.consignee_mobile = newTableData[0].consignee_mobile;
          req.consignee_pincode = newTableData[0].consignee_pincode;
          req.consignee_state = newTableData[0].consignee_state;
        } catch (c) {}

        console.log(req);
        let totalWeight = 0;
        newTableData.forEach((x) => {
          const consigneeCountry = nestedTables?.consignee_country?.find((country: any) => country.value == x.consignee_country);
          const isIndiaConsignee = consigneeCountry?.label === "India" || x.consignee_country === "IN" || x.consignee_country === "India";
          if (!x.awb_no && !req.autoGen) {
            awbError = "Missing Airwaybill info";
          }
          if (isIndiaConsignee && !/^[1-9][0-9]{5}$/.test(String(x.pincode || ""))) {
            awbError = "Invalid Indian pincode for AWB: " + (x.awb_no || "row " + x.id);
          }
          if (Number(x.weight) == 0) {
            awbError = "Weight is 0 for AWB: " + x.awb_no;
          }
          if (x.piece.length == 0) {
            if (x.consignee == "" || x.address == "") {
              awbError = "Consignee Info is missing for AWB: " + x.awb_no;
            }
            totalWeight += Number(x.weight);
          } else {
            let pieceTotalW = 0;
            x.piece.forEach((p) => {
              pieceTotalW += Number(p.weight);
              //totalWeight += Number(p.weight);
            });
            if (Number(x.weight) != pieceTotalW) {
              awbError = "Piece Total is not Matching with Total Weight for AWB: " + x.awb_no;
            }
            totalWeight += pieceTotalW;
          }
        });
        if (awbError) {
          alert(awbError);
          return;
        }
        if (totalWeight == 0) {
          alert("Total Weight is wrong!");
          return;
        }
        const shipperCountry = nestedTables?.consignee_country?.find((country: any) => country.value == req.shipper_country);
        const isIndiaShipper = shipperCountry?.label === "India" || req.shipper_country === "IN" || req.shipper_country === "India";
        if (isIndiaShipper && !/^[1-9][0-9]{5}$/.test(String(req.shipper_pincode || ""))) {
          return alert("Invalid Indian shipper pincode");
        }
        if (req.shipper_city == "" || req.shipper_pincode == "" || req.shipper_sh == "" || req.shipper_address == "") {
          return alert("Shipper Info is missing");
        }
        req.quantity = req.awb_data.length;
        req.weight = totalWeight.toString();

        //return;
        priceCheckMutate({ url: "/admin/booking/check-price", data: req });
        setBookingData(req);
      },
    });

  const {
    mutate: mutatePincode,
    isLoading: isPincodeloading,
  }: {
    mutate: UseMutateFunction<AxiosResponse<any, any>, unknown, any, unknown>;
    isLoading: any;
  } = useMutation((pincode) => GetCommanWay("/admin/address/" + pincode, {}, {}), {
    onSuccess: (data: any) => {
      if (data.status === "SUCCESS") {
        Object.keys(data.data).map((d) => {
          setFieldValue(pincodeSection + "_" + d, data.data[d]);
        });
        return data.data;
      }
    },
    onError: (data: any) => {
      typeof data === "string" ? UseToast(data, "error") : UseToast(data.message, "error");
    },
  });

  const {
    mutate: mutatePickup,
    isLoading: isPickuploading,
  }: {
    mutate: UseMutateFunction<AxiosResponse<any, any>, unknown, any, unknown>;
    isLoading: any;
  } = useMutation((pickup_code) => ListCommanWay("/admin/pickup/list", { pickup_code }, {}), {
    onSuccess: async (data: any) => {
      if (data.status === "SUCCESS") {
        if (data.data?.data?.length > 0) {
          let isBooked = false;

          try {
            let booked = await ListCommanWay(
              "/admin/booking/list",
              {
                qry: `pickup_code = '${values.pickup_code}'`,
              },
              {}
            );
            if (booked.data.data.length > 0) {
              isBooked = true;
            }
          } catch (c) {}

          if (isBooked) {
            let pickupTime = moment(data.data.data[0].pickup_date_time || data.data.data[0].date, "DD-MM-YYYY HH:mm:ss");
            if (!data.data.data[0].pickup_date_time || data.data.data[0].pickup_date_time == "Invalid Date") {
              pickupTime = moment(data.data.data[0].date, "YYYY-MM-DD");
            }
            let diff = moment().diff(pickupTime, "h");
            if (diff > 55) {
              Swal.fire({
                title: "Reference Code is expired !",
                icon: "error",
              });
              setFieldValue("pickup_code", "");
              return;
            }
          }
        }

        data.data.data.map((d) => {
          try {
            let pickupTime = moment(d.pickup_date_time || d.date, "DD-MM-YYYY HH:mm:ss");
            if (!d.pickup_date_time || d.pickup_date_time == "Invalid Date") {
              pickupTime = moment(data.data.data[0].date, "YYYY-MM-DD");
            }
            d.date = pickupTime.format("YYYY-MM-DD");
          } catch (c) {
            d.date = moment(d.date).format("YYYY-MM-DD");
          }

          Object.keys(d).map((obj) => {
            if (!["type", "createdAt"].includes(obj)) {
              setFieldValue(obj, d[obj]);
            }

            if (obj === "quantity" && Number(d[obj]) >= 0) {
              fetchDBInputData().then((value) => {
                value && handleQuantityChange({ target: { value: d[obj] } }, data.data.data);
              });
            }
          });
        });
        return data.data[0];
      }
    },
    onError: (data: any) => {
      typeof data === "string" ? UseToast(data, "error") : UseToast(data.message, "error");
    },
  });

  const {
    mutate: mutateCustomer,
    isLoading: isCustomerloading,
  }: {
    mutate: UseMutateFunction<AxiosResponse<any, any>, unknown, any, unknown>;
    isLoading: any;
  } = useMutation((code) => GetCommanWay("/admin/customer/get/" + code, {}, {}), {
    onSuccess: (data: any) => {
      if (data.status === "SUCCESS") {
        Object.keys(values).map((d) => {
          if (d.split("customer_")[1]) {
            values["customer_" + d.split("customer_")[1]] = data.data[d.split("customer_")[1]];
          }
        });
        return data.data;
      }
    },
    onError: (data: any) => {
      //typeof data === "string" ? UseToast(data, "error") : UseToast(data.message, "error");
    },
  });

  const {
    mutate: mutateBooking,
    isLoading: isBookingloading,
  }: {
    mutate: UseMutateFunction<AxiosResponse<any, any>, unknown, any, unknown>;
    isLoading: any;
  } = useMutation((code) => GetCommanWay("/admin/booking/" + code, {}, {}), {
    onSuccess: (data: any) => {
      if (data.status === "SUCCESS") {
        console.log("data.data", data.data);

        setNewTableData(data.data.awb_data);
        return data.data;
      }
    },
    onError: (data: any) => {
      typeof data === "string" ? UseToast(data, "error") : UseToast(data.message, "error");
    },
  });

  const {
    mutate: updateAwb,
    isLoading: isUpdateAwbloading,
  }: {
    mutate: UseMutateFunction<AxiosResponse<any, any>, unknown, any, unknown>;
    isLoading: any;
  } = useMutation(
    ({ data, bookingId }) =>
      UpdateBookingCommanWay({
        url: "/admin/awbdata/updateBulk",
        data,
        query: {
          assign_to_booking: bookingId,
        },
        options: {},
      }),
    {
      onSuccess: (data: any) => {
        if (data.status === "SUCCESS") {
          console.log("data.data", data.data);

          setNewTableData(data.data);
          return data.data;
        }
      },
      onError: (data: any) => {
        typeof data === "string" ? UseToast(data, "error") : UseToast(data.message, "error");
      },
    }
  );

  const fetchDBInputData = async () => {
    const proms: Promise<number>[] = [];
    //console.log(JSON.stringify(nestedTables));
    if (JSON.stringify(nestedTables).length > 5) {
      return Promise.all(proms).then((x) => {
        return 1;
      });
    }
    console.log("asdadasdasdas");

    let nestedApiData = {};
    columnData.forEach((cd: any) => {
      if (cd.type == ADT.DB) {
        proms.push(
          new Promise((res, rej) => {
            ListCommanWay(`/admin/${cd.db}/list`, {}, { paginate: 1000 })
              .then((x) => {
                let data = [];
                if (x.status == "SUCCESS") {
                  data = x.data.data.map((d) => {
                    return {
                      value: cd.key == "consignee_country" ? d.country_code : cd?.needID === false ? d[cd.db_col] : d.id,
                      label: d[cd.db_col],
                    };
                  });
                  nestedApiData[cd.key] = data;
                  cd["options"] = data;
                }
                res(1);
              })
              .catch((c) => {
                res(0);
              });
          })
        );
      }
    });
    return Promise.all(proms).then((x) => {
      console.log("nestedApiData", nestedApiData);
      nestedApiData["placeholder"] = [1, 2, 3, 4, 5];
      setNestedTables(nestedApiData);
      return 1;
    });
  };

  const modeActions = () => {
    if (mode) {
      console.log("data", data);

      if (mode === "edit") {
        mutateBooking(data.id);
        Object.keys(data).map((obj) => {
          if (!["createdAt"].includes(obj)) values[obj] = data[obj];

          if (obj === "quantity" && Number(data[obj]) >= 0) {
            fetchDBInputData().then((value) => {
              value &&
                handleQuantityChange({
                  target: { value: data[obj] },
                });
            });
          }
          if (obj === "weight" && Number(data[obj]) >= 0) {
            values[obj] = Number(data[obj]);
          }
          if (obj === "date") {
            console.log(
              "moment(new Date(data[obj])).format(YYYY-DD-MM)",
              data[obj],
              moment(data[obj], "DD-MM-YYYY HH:mm:ss").format("YYYY-DD-MM")
            );
            values[obj] = moment(data[obj], "DD-MM-YYYY HH:mm:ss").format("YYYY-MM-DD");
          }
        });
      } else if (mode === "view") {
        mutateBooking(data.id);

        Object.keys(data).map((obj) => {
          if (!["createdAt"].includes(obj)) values[obj] = data[obj];

          if (obj === "quantity" && Number(data[obj]) >= 0) {
            fetchDBInputData().then((value) => {
              value &&
                handleQuantityChange({
                  target: { value: data[obj] },
                });
            });
          }
          if (obj === "weight" && Number(data[obj]) >= 0) {
            values[obj] = Number(data[obj]);
          }
          if (obj === "date") {
            values[obj] = moment(data[obj], "DD-MM-YYYY HH:mm:ss").format("YYYY-MM-DD");
          }
        });
      }
    }
  };

  const [subbranchOptions, setSubBranchOptions] = useState<any>([{ value: 0, label: "Loading..." }]);

  const {
    error: subbranchError,
    isFetching: subbranchIsFetching,
    isError: subbranchIsError,
    isFetched: subbranchIsFetched,
    isLoading: subbranchIsLoading,
    refetch: subbranchRefetch,
  } = useQuery(["SubBranchesListApi"], () => ListCommanWay("/admin/subbranch/list", {}, { paginate: 1000 }), {
    keepPreviousData: true,
    onSuccess(data) {
      if (data.status == "SUCCESS") {
        let opt:any = [];
        data.data.data.forEach((p) => {
          opt.push({
            value: p.id,
            label: p.branch_name,
          });
        });
        setSubBranchOptions(opt);
      }
    },
  });

  useEffect(() => {
    modeActions();
    handleQuantityChange({
      target: { value: values.quantity },
    });
  }, []);
  console.log("-=======-----> newTableData", newTableData);

  const getInput: any = (type, label, value, name, isDisabled, width) => {
    let ip = <div></div>;

    switch (type) {
      case ADT.NUMBER:
        ip = (
          <div className={`inline-flex items-center gap-2 ${width ? width : "w-auto"}`}>
            <label className=" text-sm font-semibold text-gray-900">{label}</label>
            <input
              type="number"
              className={`${
                isDisabled ? "bg-gray-300" : ""
              } w-10 bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-1`}
              placeholder={label}
              name={name}
              value={values[name]}
              onBlur={handleBlur}
              max={name == "quantity" ? 99 : 9999999}
              onChange={(e) => {
                if (Number(e.target.value) >= 0) {
                  handleChange(e);
                  if (name === "quantity") {
                    fetchDBInputData().then((value) => {
                      value && handleQuantityChange(e);
                    });
                  }
                }
              }}
              onWheel={handleWheel}
              disabled={mode && (mode === "view" || mode === "edit") ? true : isDisabled}
            />
          </div>
        );
        break;
      case ADT.TEXT:
        ip = (
          <div className={`inline-flex items-center gap-2 ${width ? width : "w-auto"}`}>
            <label className="text-sm font-semibold text-gray-900 whitespace-nowrap">{label}</label>
            <input
              type="text"
              className={`bg-gray-50 border  text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-1 ${
                isDisabled ? "bg-gray-300 border-red" : "border-gray-300"
              }`}
              placeholder={label}
              name={name}
              value={values[name]}
              onBlur={async (e) => {
                handleBlur(e);
                if (label === "Pincode") {
                  setPincodeSection(e.target.name.split("_")[0]);
                  if (e.target.value.length == 6) {
                    const responseData = mutatePincode(e.target.value);
                  }
                }
                if (name === "customer_code" && !values.pickup_code) {
                  const responseData = mutateCustomer(e.target.value);
                }
                if (name === "pickup_code") {
                  const responseData = mutatePickup(e.target.value);
                }
              }}
              onChange={handleChange}
              disabled={mode && (mode === "view" || mode === "edit") ? true : isDisabled}
            />
          </div>
        );
        break;
      case ADT.DB:
        ip = (
          <div className={`inline-flex items-center gap-2 ${width ? width : "w-auto"}`}>
            <label className="text-sm font-semibold text-gray-900 whitespace-nowrap">{label}</label>
            <Select
              className={`${isDisabled ? "bg-gray-300" : ""} bg-gray-50 border-gray-300 text-gray-900 text-sm rounded-lg block w-full`}
              options={value.all}
              onBlur={handleBlur}
              onChange={(e) => {
                if (name === "branch_name") {
                  setIsBranchSet(e.value);
                }
                setFieldValue(name, e);
              }}
              classNamePrefix="book_dd"
              name={name}
              value={values[name]}
              isDisabled={mode && (mode === "view" || mode === "edit") ? true : isDisabled}
              styles={{
                control: (base) => ({
                  ...base,
                  height: 30,
                  minHeight: 30,
                }),
                indicatorsContainer: (base) => ({
                  ...base,
                  "& .book_dd__indicator": {
                    padding: "2px",
                  },
                }),
                input: (base) => ({
                  ...base,
                  margin: 0,
                  padding: 0,
                }),
                valueContainer: (base) => ({
                  ...base,
                  paddingTop: 0,
                  paddingBottom: 0,
                }),
              }}
            />
          </div>
        );
        break;
      case ADT.TEXTAREA:
        break;
      case ADT.DATETIME:
        break;
      case ADT.DATE:
        ip = (
          <div className={`inline-flex items-center gap-2 ${width ? width : "w-auto"}`}>
            <label className="text-sm font-semibold text-gray-900 whitespace-nowrap">{label}</label>
            <input
              type="date"
              className={`${
                isDisabled ? "bg-gray-300" : ""
              } bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-1`}
              placeholder={label}
              name={name}
              value={values[name]}
              onBlur={handleBlur}
              onChange={handleChange}
              disabled={mode && (mode === "view" || mode === "edit") ? true : isDisabled}
            />
          </div>
        );
        break;
      case ADT.FILE:
        break;
      default:
        ip = <></>;
        break;
    }
    return ip;
  };
  return (
    <div className="bg-gradient-to-br from-purple-400 via-blue-50 to-primary/50 py-2 px-2 font-inter flex-1">
      {(isupdateBookingloading || isloading) && <Spinner />}

      {/* ═══════════ HEADER BAR ═══════════ */}
      <div className="mx-auto mb-2">
        <div className="bg-white/80 backdrop-blur-sm rounded-2xl px-3 py-2 shadow-xl shadow-slate-200/50">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="flex-1"></div>
            <h1 className="text-base font-bold bg-gradient-to-r from-primary to-amber-600 bg-clip-text text-transparent uppercase">
              Add International Booking
            </h1>
            <div className="flex-1"></div>
          </div>
        </div>
      </div>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          handleSubmit(e);
        }}
      >
        {/* ═══════════ BOOKING FORM - FULL WIDTH ═══════════ */}
        <div className="shadow-xl shadow-teal-900/20 bg-[#009688] overflow-visible rounded-xl mb-2">
          <div className="pb-0 pt-2 px-3">
            <div className="flex items-center justify-between gap-2 w-full flex-wrap">
              <div className="flex items-center gap-2 flex-wrap">
                <div className="w-7 h-7 rounded-lg bg-white/20 flex items-center justify-center">
                  <svg className="w-3.5 h-3.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                  </svg>
                </div>
                <h2 className="text-sm font-bold text-white">Booking Form</h2>
                <span className="text-white/40 mx-1">|</span>
                <div className="flex items-center gap-2">
                  <span className="text-[11px] font-medium text-white">Type:</span>
                  <label className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border cursor-pointer transition-all text-[12px] font-medium ${values.type === "PU" ? "border-white bg-white text-[#009688] font-bold shadow-sm" : "border-white/40 bg-white/10 text-white hover:bg-white/20"}`}>
                    <input
                      type="radio"
                      value="PU"
                      name="type"
                      checked={values.type === "PU"}
                      onChange={(e) => setFieldValue("type", e.target.value)}
                      disabled={!!(mode && (mode === "view" || mode === "edit"))}
                      className="w-3.5 h-3.5 text-[#009688] focus:ring-white/20"
                    />
                    P/U
                  </label>
                  <label className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border cursor-pointer transition-all text-[12px] font-medium ${values.type === "WI" ? "border-white bg-white text-[#009688] font-bold shadow-sm" : "border-white/40 bg-white/10 text-white hover:bg-white/20"}`}>
                    <input
                      type="radio"
                      value="WI"
                      name="type"
                      checked={values.type === "WI"}
                      onChange={(e) => setFieldValue("type", e.target.value)}
                      disabled={!!(mode && (mode === "view" || mode === "edit"))}
                      className="w-3.5 h-3.5 text-[#009688] focus:ring-white/20"
                    />
                    W/I
                  </label>
                </div>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                {Object.keys(userDetails).length &&
                (userDetails?.roleName === roles.SUPER_ADMIN || userDetails?.roleName === roles.BRANCH_ADMIN) ? (
                  <MSelect
                    label=""
                    name="subbranch"
                    options={subbranchOptions}
                    value={values.subbranch}
                    onChange={(e: any) => setFieldValue("subbranch", e)}
                    onBlur={handleBlur}
                    disabled={isDisabled}
                    className="min-w-[140px]"
                  />
                ) : null}
              </div>
            </div>
          </div>
          <hr className="mt-1.5 border-white/20" />
          <div className="px-3 py-2">
            <div className="grid grid-cols-4 xl:grid-cols-4 lg:grid-cols-3 md:grid-cols-2 sm:grid-cols-1 gap-x-3 gap-y-2 items-center">
              {values.type === "PU" && (
                <MInput
                  label="Ref Code"
                  name="pickup_code"
                  value={values.pickup_code}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  onBlurExtra={(e: any) => mutatePickup(e.target.value)}
                  disabled={mode && (mode === "view" || mode === "edit") ? true : isDisabled}
                  labelClassName="text-white font-medium"
                />
              )}
              <div className="flex items-center gap-1.5">
                <label className="text-[11px] font-medium text-white whitespace-nowrap min-w-[52px] text-right">Date</label>
                <input
                  type="date"
                  className={`flex-1 min-w-0 px-2 py-[3px] rounded-lg border text-[13px] outline-none transition-all ${
                    mode && (mode === "view" || mode === "edit") ? true : isDateDisabled
                      ? "border-slate-200 bg-slate-100 text-slate-500"
                      : "border-slate-200 bg-white focus:border-primary2 focus:ring-2 focus:ring-primary2/20 text-slate-700"
                  }`}
                  name="date"
                  value={values.date}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  disabled={mode && (mode === "view" || mode === "edit") ? true : isDateDisabled}
                />
              </div>
              <MInput
                label="Qty"
                name="quantity"
                type="number"
                value={values.quantity}
                onChange={(e: any) => {
                  if (Number(e.target.value) >= 0) {
                    handleChange(e);
                    fetchDBInputData().then((value) => {
                      value && handleQuantityChange(e);
                    });
                  }
                }}
                onBlur={handleBlur}
                disabled={mode && (mode === "view" || mode === "edit") ? true : isDisabled}
                max={99}
                labelClassName="text-white font-medium"
              />
              <MInput
                label="Weight(KG)"
                name="weight"
                type="number"
                value={values.weight}
                onChange={(e: any) => {
                  if (Number(e.target.value) >= 0) {
                    handleChange(e);
                  }
                }}
                onBlur={handleBlur}
                disabled={mode && (mode === "view" || mode === "edit") ? true : isDisabled}
                labelClassName="text-white font-medium"
              />
              <MInput
                label="Customer"
                name="customer_code"
                value={values.customer_code}
                onChange={handleChange}
                onBlur={handleBlur}
                onBlurExtra={(e: any) => {
                  if (!values.pickup_code) {
                    mutateCustomer(e.target.value);
                  }
                }}
                disabled={mode && (mode === "view" || mode === "edit") ? true : isDisabled}
                placeholder="Customer Code"
                labelClassName="text-white font-medium"
              />
              <MInput
                label="Remarks"
                name="remarks"
                value={values.remarks}
                onChange={handleChange}
                onBlur={handleBlur}
                disabled={mode && (mode === "view" || mode === "edit") ? true : isDisabled}
                labelClassName="text-white font-medium"
              />
              <MSelect
                label="Provider"
                name="provider"
                options={[
                  { label: "UPS SAVER", value: "UPS SAVER" },
                  { label: "DHL", value: "DHL" },
                  { label: "FEDEX EXPORT", value: "FEDEX EXPORT" },
                  { label: "ARAMEX EXPORT", value: "ARAMEX EXPORT" },
                  { label: "Aramex UAE", value: "Aramex UAE" },
                ]}
                value={values.provider}
                onChange={(e: any) => setFieldValue("provider", e)}
                onBlur={handleBlur}
                disabled={mode && (mode === "view" || mode === "edit") ? true : isDisabled}
                labelClassName="text-white font-medium"
              />
              <MCheckbox
                label="GST Billing"
                name="billingDiffFromShipping"
                value={billingDiffFromShipping}
                onChange={(e: any) => {
                  setBillingDiffFromShipping(e.target.checked);
                  if (e.target.checked) {
                    setFieldValue("customer_code", "");
                    setFieldValue("customer_sh", "");
                    setFieldValue("customer_pincode", "");
                    setFieldValue("customer_city", "");
                    setFieldValue("customer_state", "");
                    setFieldValue("customer_country", "");
                    setFieldValue("customer_address", "");
                    setFieldValue("customer_mobile", "");
                  } else {
                    setFieldValue("billing_name", "");
                    setFieldValue("billing_address", "");
                    setFieldValue("billing_city", "");
                    setFieldValue("billing_state", "");
                    setFieldValue("billing_pincode", "");
                    setFieldValue("billing_country", "India");
                    setFieldValue("billing_gst_no", "");
                    setFieldValue("billing_mobile", "");
                  }
                }}
                disabled={!!(mode && (mode === "view" || mode === "edit"))}
                className="border-white/40 bg-white/10 hover:bg-white/20 text-white"
                labelClassName="text-white font-medium"
              />
                <MCheckbox
                  label="Auto Generate"
                  name="autoGen"
                  value={values.autoGen}
                  onChange={(e: any) => {
                    setFieldValue("autoGen", e.target.checked ? 1 : 0);
                    setNewTableData((rows: any[]) => rows.map((row) => ({ ...row, autoGen_product: e.target.checked ? "I" : "" })));
                  }}
                  disabled={!!(mode && (mode === "view" || mode === "edit"))}
                  className="border-white/40 bg-white/10 hover:bg-white/20 text-white"
                  labelClassName="text-white font-medium"
                />
            </div>
          </div>
        </div>

        {/* ═══════════ CUSTOMER & SHIPPER - SIDE BY SIDE ═══════════ */}
        <div className="grid grid-cols-2 md:grid-cols-1 gap-2">
          {/* Customer Info Card - LEFT (shown when billingDiffFromShipping is false = normal mode) */}
          {!billingDiffFromShipping && (
            <div className="shadow-xl shadow-slate-200/50 bg-white/80 backdrop-blur-sm overflow-visible rounded-xl">
              <div className="p-0">
                <div className="flex">
                  <div className="w-7 flex-shrink-0 bg-primary/50 rounded-l-xl flex items-center justify-center">
                    <span className="text-[10px] font-bold text-white tracking-wider [writing-mode:vertical-lr] rotate-180">CUSTOMER</span>
                  </div>
                  <div className="flex-1 px-2.5 py-2 space-y-1">
                    <div className="grid grid-cols-3 gap-x-2 gap-y-1">
                      <MInput label="Mobile" name="customer_mobile" value={values.customer_mobile} onChange={handleChange} onBlur={handleBlur} disabled />
                      <MInput label="Pincode" name="customer_pincode" value={values.customer_pincode} onChange={handleChange} onBlur={handleBlur} disabled />
                      <MInput label="S/H" name="customer_sh" value={values.customer_sh} onChange={handleChange} onBlur={handleBlur} disabled />
                    </div>
                    <div className="grid grid-cols-3 gap-x-2 gap-y-1">
                      <MInput label="City" name="customer_city" value={values.customer_city} onChange={handleChange} onBlur={handleBlur} disabled />
                      <MInput label="State" name="customer_state" value={values.customer_state} onChange={handleChange} onBlur={handleBlur} disabled />
                      <MInput label="Country" name="customer_country" value={values.customer_country} onChange={handleChange} onBlur={handleBlur} disabled />
                    </div>
                    <MInput label="Address" name="customer_address" value={values.customer_address} onChange={handleChange} onBlur={handleBlur} disabled />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Billing Info Card - LEFT (shown when billingDiffFromShipping is true = GST billing) */}
          {billingDiffFromShipping && (
            <div className="shadow-xl shadow-slate-200/50 bg-white/80 backdrop-blur-sm overflow-visible rounded-xl">
              <div className="p-0">
                <div className="flex">
                  <div className="w-7 flex-shrink-0 bg-amber-500/70 rounded-l-xl flex items-center justify-center">
                    <span className="text-[10px] font-bold text-white tracking-wider [writing-mode:vertical-lr] rotate-180">BILLING</span>
                  </div>
                  <div className="flex-1 px-2.5 py-2 space-y-1">
                    <div className="flex items-center gap-1.5">
                      <MInput
                        label="GST No"
                        name="billing_gst_no"
                        value={values.billing_gst_no}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        onBlurExtra={(e: any) => handleGstSearch(e.target.value)}
                        className="flex-1"
                        placeholder="Enter GSTIN"
                      />
                      {gstSearching && <span className="text-[10px] text-blue-500 animate-pulse">Searching...</span>}
                    </div>
                    <MInput label="Name" name="billing_name" value={values.billing_name} onChange={handleChange} onBlur={handleBlur} />
                    <MInput label="Mobile" name="billing_mobile" type="number" value={values.billing_mobile} onChange={handleChange} onBlur={handleBlur} />
                    <MInput label="Address" name="billing_address" value={values.billing_address} onChange={handleChange} onBlur={handleBlur} />
                    <div className="grid grid-cols-3 gap-x-2 gap-y-1">
                      <MInput
                        label="Pincode"
                        name="billing_pincode"
                        value={values.billing_pincode}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        onBlurExtra={(e: any) => {
                          setPincodeSection("billing");
                          if (e.target.value.length == 6) mutatePincode(e.target.value);
                        }}
                      />
                      <MInput label="City" name="billing_city" value={values.billing_city} onChange={handleChange} onBlur={handleBlur} />
                      <MInput label="State" name="billing_state" value={values.billing_state} onChange={handleChange} onBlur={handleBlur} />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Shipper Info Card - RIGHT */}
          <div className="shadow-xl shadow-slate-200/50 bg-white/80 backdrop-blur-sm overflow-visible rounded-xl">
            <div className="p-0">
              <div className="flex">
                <div className="w-7 flex-shrink-0 bg-primary/50 rounded-l-xl flex items-center justify-center">
                  <span className="text-[10px] font-bold text-white tracking-wider [writing-mode:vertical-lr] rotate-180">SHIPPER</span>
                </div>
                <div className="flex-1 px-2.5 py-2 space-y-1">
                  <div className="grid grid-cols-3 gap-x-2 gap-y-1">
                    <MInput
                      label="Mobile"
                      name="shipper_mobile"
                      value={values.shipper_mobile}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      disabled={mode && (mode === "view" || mode === "edit") ? true : isDisabled}
                    />
                    <MInput
                      label="Pincode"
                      name="shipper_pincode"
                      value={values.shipper_pincode}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      onBlurExtra={(e: any) => {
                        setPincodeSection("shipper");
                        if ((values.shipper_country === "IN" || values.shipper_country === "India") && e.target.value.length == 6) {
                          mutatePincode(e.target.value);
                        }
                      }}
                      disabled={mode && (mode === "view" || mode === "edit") ? true : isDisabled}
                    />
                    <MInput label="S/H" name="shipper_sh" value={values.shipper_sh} onChange={handleChange} onBlur={handleBlur} disabled={mode && (mode === "view" || mode === "edit") ? true : isDisabled} />
                    <MInput label="City" name="shipper_city" value={values.shipper_city} onChange={handleChange} onBlur={handleBlur} disabled={values.shipper_country === "IN" || values.shipper_country === "India"} />
                    <MInput label="State" name="shipper_state" value={values.shipper_state} onChange={handleChange} onBlur={handleBlur} disabled={values.shipper_country === "IN" || values.shipper_country === "India"} />
                    <MSelect
                      label="Country"
                      name="shipper_country"
                      options={nestedTables?.consignee_country || []}
                      value={(nestedTables?.consignee_country || []).find((option: any) => option.value === values.shipper_country) || null}
                      onChange={(option: any) => {
                        setFieldValue("shipper_country", option?.value || "");
                        if (option?.value !== "IN" && option?.label !== "India") {
                          setFieldValue("shipper_city", "");
                          setFieldValue("shipper_state", "");
                        }
                      }}
                      onBlur={handleBlur}
                      disabled={mode && (mode === "view" || mode === "edit") ? true : isDisabled}
                      labelClassName="text-slate-500"
                    />
                  </div>
                  <MInput
                    label="Address"
                    name="shipper_address"
                    value={values.shipper_address}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    disabled={mode && (mode === "view" || mode === "edit") ? true : isDisabled}
                  />
                  <div className="hidden">
                    <MCheckbox
                      label="Change Shipper"
                      name="change_shipper"
                      value={values.change_shipper}
                      onChange={(e: any) => setFieldValue("change_shipper", e.target.checked)}
                      disabled={mode && (mode === "view" || mode === "edit") ? true : isDisabled}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Consignee Information - Preserved & Hidden */}
        <div className="hidden">
          <MInput label="Mobile" name="consignee_mobile" value={values.consignee_mobile} onChange={handleChange} onBlur={handleBlur} disabled={isDisabled} />
          <MInput label="Pincode" name="consignee_pincode" value={values.consignee_pincode} onChange={handleChange} onBlur={handleBlur} disabled={isDisabled} />
          <MInput label="Cgnee" name="consignee_cgnee" value={values.consignee_cgnee} onChange={handleChange} onBlur={handleBlur} disabled={isDisabled} />
          <MInput label="City" name="consignee_city" value={values.consignee_city} onChange={handleChange} onBlur={handleBlur} disabled />
          <MInput label="State" name="consignee_state" value={values.consignee_state} onChange={handleChange} onBlur={handleBlur} disabled />
          <MInput label="Country" name="consignee_country" value={values.consignee_country} onChange={handleChange} onBlur={handleBlur} disabled />
          <MInput label="Address" name="consignee_address" value={values.consignee_address} onChange={handleChange} onBlur={handleBlur} disabled={isDisabled} />
        </div>

        {/* ═══════════ ACTION BUTTONS ═══════════ */}
        <div className="shadow-xl shadow-slate-200/50 bg-slate-100/80 backdrop-blur-sm overflow-visible rounded-xl px-3 py-2 mt-2">
          <div className="flex items-center justify-end gap-3 flex-wrap">
            {mode && mode === "view" ? (
              <Button
                type="button"
                size="sm"
                color="secondary"
                variant="solid"
                className="h-8 text-xs font-semibold"
                onPress={() => navigate(-1)}
              >
                Go Back
              </Button>
            ) : mode && mode === "edit" ? (
              <Button
                type="submit"
                size="sm"
                className="bg-gradient-to-r from-[#001f3f] to-[#003366] text-white font-bold rounded-lg shadow-lg shadow-[#001f3f]/30 h-8 text-xs tracking-wide hover:shadow-xl hover:shadow-[#001f3f]/40 transition-all px-6"
                isLoading={isSubmitting || isloading || isupdateBookingloading}
              >
                Update
              </Button>
            ) : (
              <Button
                type="submit"
                size="sm"
                className="bg-gradient-to-r from-[#001f3f] to-[#003366] text-white font-bold rounded-lg shadow-lg shadow-[#001f3f]/30 h-8 text-xs tracking-wide hover:shadow-xl hover:shadow-[#001f3f]/40 transition-all px-6"
                isLoading={isSubmitting || isloading || isupdateBookingloading}
              >
                Save
              </Button>
            )}
          </div>
        </div>

        {/* ═══════════ AWB DATA TABLE ═══════════ */}
        <div className="mt-3">
          <div className="shadow-xl shadow-slate-200/50 bg-white/80 rounded-xl overflow-hidden">
            <div className="px-3 py-2 border-b border-slate-200 bg-slate-50/80">
              <div className="flex items-center gap-2">
                <div className="w-6 h-6 rounded-md bg-gradient-to-br from-green-400 to-green-600 flex items-center justify-center">
                  <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
                <span className="text-xs font-bold text-slate-800">AWB Data</span>
                <span className="text-[10px] text-slate-500">({newTableData.length} items)</span>
              </div>
            </div>
            <div className="overflow-x-auto">
              <IntlBookingInputTable
                columnData={columnData}
                tableData={newTableData}
                setNewTableData={setNewTableData}
                customClasses="flex-1"
                mode={mode}
                autoGen={values.autoGen === 1}
              />
            </div>
          </div>
        </div>
      </form>
    </div>
  );
};

export default AddIntlBooking;
